import { useState } from "react";
import ModalShell from "../../../../_components/modal/ModalShell";
import UpsellWizardBody from "../../components/UpsellWizardBody";
import { UPSELL_WIZARD_STEPS } from "../../constants";
import { useUpsellWizard } from "../../hooks/useUpsellWizard";
import { usePricingState } from "../../../../_components/billing";
import { useGetUsersByOrgIdQuery } from "../../../../_features/userManagement/api/userApiSlice";
import { useGetAllPlanByOrgIdQuery } from "../../../../_features/plans/api/planApi";
import { useGetAllProductByOrgIdQuery } from "../../../../_features/products/api/productApi";
import { useGetAllBundlesQuery } from "../../../../_features/plans/api/bundleApiSlice";
import ContactsPickerModal from "../../../../_components/contacts/ContactsPickerModal";
import { useEnsureTemplateDefaultsOnMount } from "../../../documentManagement/hooks/useEnsureTemplateDefaultsOnMount";
import "./_upsell-form-modal.scss";

/**
 * True-modal host - mirrors _features/proposals/modals/ProposalFormModal
 * exactly. Opened from CompanyUpsells ("+ Add upsell/downsell" on a
 * company's own page) and SubscriptionUpsells (same, on a subscription's
 * page) - both pass `seedData` to pre-fill the company/subscription
 * pickers instead of a full existing record.
 */
const UpsellFormModal = ({ isOpen, onClose, mode = "create", initialData, seedData, onSuccess }) => {
  const [isSendToPickerOpen, setIsSendToPickerOpen] = useState(false);

  // A brand-new org has no Default Upsell template until someone visits
  // Settings -> Document Templates. Seed it here too so "Create
  // upsell/downsell" doesn't 404 with TemplateNotFoundError on an org
  // that's never opened that settings page - same fix as InvoiceFormModal.
  useEnsureTemplateDefaultsOnMount();

  const { data: allUsers } = useGetUsersByOrgIdQuery({});
  const { data: allPlans } = useGetAllPlanByOrgIdQuery({});
  const { data: allProducts } = useGetAllProductByOrgIdQuery({});
  const { data: allBundles } = useGetAllBundlesQuery({});

  // `plans`/`products` deliberately left undefined (not `|| []`) while
  // their queries are still in flight - useUpsellWizard's pricing-hydration
  // effect uses that to tell "not loaded yet" apart from "genuinely empty",
  // see its own docstring.
  const catalogs = { plans: allPlans, products: allProducts, bundles: allBundles?.data || [] };
  const pricing = usePricingState();
  const wizard = useUpsellWizard({ mode, initialData, seedData, pricing, catalogs, onSuccess, onClose });

  if (!isOpen) return null;

  const isLastStep = wizard.currentStep === UPSELL_WIZARD_STEPS.length;
  const headerButtons = [
    {
      children: "Back",
      variant: "outlined",
      size: "sm",
      onClick: wizard.goToPreviousStep,
      disabled: wizard.currentStep === 1,
    },
    isLastStep
      ? wizard.isPreview
        ? { children: "Close", variant: "contained", size: "sm", onClick: onClose }
        : {
            children: wizard.isEdit ? "Update" : "Create",
            variant: "contained",
            size: "sm",
            onClick: wizard.onSubmit,
            loading: wizard.isSubmitting,
          }
      : {
          children: "Next",
          variant: "contained",
          size: "sm",
          onClick: wizard.goToNextStep,
        },
  ];

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onRequestClose={onClose}
        title={
          wizard.isPreview
            ? "Preview upsell/downsell"
            : wizard.isEdit
            ? "Edit upsell/downsell"
            : "Create upsell/downsell"
        }
        headerButtons={headerButtons}
        width="100vw"
        height="100vh"
        center
        className="upsell-form-modal--fullscreen"
        isSubmitting={wizard.isSubmitting}
      >
        <UpsellWizardBody
          wizard={wizard}
          catalogs={catalogs}
          users={allUsers || []}
          onAddSendToContact={() => setIsSendToPickerOpen(true)}
          showChrome={false}
        />
      </ModalShell>

      {isSendToPickerOpen && (
        <ContactsPickerModal
          onClose={() => setIsSendToPickerOpen(false)}
          onSubmit={(contacts) => {
            wizard.setSendTo(contacts);
            setIsSendToPickerOpen(false);
          }}
          companyData={{ company: wizard.watch("company") }}
          context="upsell"
          existingRecipients={wizard.watch("sendTo") || []}
          cardText="Select contacts to send this upsell to"
        />
      )}
    </>
  );
};

export default UpsellFormModal;
